import { Field, Formik, FormikHelpers } from 'formik'

import Box from '@totallymoney/ui/components/Box'
import Button from '@totallymoney/ui/components/Button'
import Text from '@totallymoney/ui/components/Text'
import theme from '@totallymoney/ui/theme'

import { LoanTableFilterFormValues } from '@appTypes/App.interface'

import FormikTextField from '@components/form/FormikTextField'

import { loanPurposeOptions } from '@config/loanPurposeOptions'

import useWindowSize from '@hooks/useWindowSize'

import { LoanFilterLimits } from '@utils/getCategoryLoanFilterLimits'

import LoanPurposeSelect from './components/LoanPurposeSelect'

interface LoansTableFilterFormProps {
    formValues: LoanTableFilterFormValues
    loanFilterLimits: LoanFilterLimits
    onFormSubmit: (
        formValues: LoanTableFilterFormValues,
        formikHelpers: FormikHelpers<LoanTableFilterFormValues>
    ) => void
    showLoanPurpose?: boolean
    validationSchema: any
}

const LoansTableFilterForm = ({
    formValues,
    loanFilterLimits,
    validationSchema,
    showLoanPurpose,
    onFormSubmit,
}: LoansTableFilterFormProps) => {
    const { width } = useWindowSize()
    const isLargeScreen = width != null && width > 768

    return (
        <Formik
            initialValues={formValues}
            onSubmit={onFormSubmit}
            validate={validationSchema}
            validateOnBlur={true}
            validateOnChange={false}
        >
            {({ submitForm, isSubmitting, setFieldValue, status }) => (
                <>
                    <Box
                        display="grid"
                        width="100%"
                        gridTemplateColumns={['1fr', '1fr', '1fr 212px']}
                        gridGap="space20"
                    >
                        <Box
                            display="flex"
                            flexDirection={['column', 'row']}
                            width="100%"
                            gridGap="space20"
                        >
                            <Box
                                flex={1}
                                maxWidth={
                                    showLoanPurpose ? ['100%', '150px'] : []
                                }
                                minWidth="125px"
                            >
                                <Field
                                    name="loanAmount"
                                    labelText="Loan amount"
                                    helperText={
                                        isLargeScreen
                                            ? undefined
                                            : `£${loanFilterLimits.loanAmount.min} - £${loanFilterLimits.loanAmount.max}`
                                    }
                                    component={FormikTextField}
                                    prefix="£"
                                    type="number"
                                    onFocus={() =>
                                        setFieldValue('loanAmount', '')
                                    }
                                />
                            </Box>
                            <Box
                                flex={1}
                                maxWidth={
                                    showLoanPurpose ? ['100%', '150px'] : []
                                }
                                minWidth="125px"
                            >
                                <Field
                                    name="loanTerm"
                                    labelText="Loan term"
                                    helperText={
                                        isLargeScreen
                                            ? undefined
                                            : `${loanFilterLimits.loanTerm.min} - ${loanFilterLimits.loanTerm.max} months`
                                    }
                                    component={FormikTextField}
                                    suffix="months"
                                    type="number"
                                    onFocus={() =>
                                        setFieldValue('loanTerm', '')
                                    }
                                />
                            </Box>
                            {showLoanPurpose ? (
                                <Box flex="1 1 auto">
                                    <LoanPurposeSelect
                                        name="loanPurpose"
                                        labelText="Loan purpose"
                                        options={loanPurposeOptions}
                                    />
                                </Box>
                            ) : null}
                        </Box>

                        <Box
                            display="flex"
                            flex={1}
                            alignItems="flex-end"
                            // Accommodate if error messages appear
                            height={[
                                'auto',
                                'auto',
                                isLargeScreen ? '80px' : '107px',
                            ]}
                        >
                            <Button
                                variant="primary"
                                text="Update results"
                                type="submit"
                                loading={isSubmitting}
                                onClick={() => submitForm()}
                            />
                        </Box>
                    </Box>

                    {status ? (
                        <Box mt="space10">
                            <Text style={{ color: theme.errorDark }}>
                                Something went wrong while submitting the form,
                                please try again.
                            </Text>
                        </Box>
                    ) : null}
                </>
            )}
        </Formik>
    )
}

export default LoansTableFilterForm
